import { Alert, Button } from "@mui/material";
import { useContextMutator } from "@openfeature/react-sdk";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { PageLoading } from "../../components/utils/PageLoading";
import { useLogout } from "../../hooks/useLogout";
import { useNotifications } from "../../hooks/useNotifications";
import { useAccessibleShops } from "../../hooks/useAccessibleShops";
import type { ShopData } from "../../types/shopData.type";
import { ShopContext } from "./shop-context";

export const ShopProvider = ({ children }: PropsWithChildren) => {
  const { data: shops, isLoading, isError, isSuccess, refetch } = useAccessibleShops();
  const { pathname, search, hash, state, key: locationKey } = useLocation();
  const { shopId: pathShopId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();
  const logout = useLogout();
  const { t } = useTranslation();
  const { setContext } = useContextMutator({ defaultContext: true });
  const lastNotifiedLocationKey = useRef<string>();
  const lastRequestedCorrection = useRef<string>();
  const hasHandledEmptyCatalog = useRef(false);
  const previousShopId = useRef<number>();

  const requestedShopId = pathShopId;
  const shop = isSuccess ? resolveAccessibleShop(shops, requestedShopId) : undefined;
  const shopId = shop?.shopId;
  const pagePath = "/" + pathname.split("/").slice(3).join("/");
  const canonicalPath = shopId === undefined ? undefined : `/shop/${shopId}${pagePath}`;
  const canonicalSearch = getSearchWithoutShopId(search);
  const locationIsCanonical =
    pathname === canonicalPath && !new URLSearchParams(search).has("shop");
  const hasNoAccessibleShops = isSuccess && shops.length === 0;
  const requestedShopIsSelected = Boolean(requestedShopId) && Number(requestedShopId) === shopId;

  // Update the URL when the user selects another shop.
  const requestShopChange = useCallback(
    (nextShopId: number) => {
      if (!shops?.some(({ shopId }) => shopId === nextShopId)) return;
      void navigate(
        { pathname: `/shop/${nextShopId}${pagePath}`, search: canonicalSearch, hash },
        { state }
      );
    },
    [navigate, pagePath, canonicalSearch, hash, state, shops]
  );

  useEffect(updateFeatureShop, [shopId, setContext]);
  useEffect(clearFeatureShopOnUnmount, [setContext]);
  useEffect(correctShopInUrl, [
    shopId,
    requestedShopIsSelected,
    requestedShopId,
    locationKey,
    showNotification,
    t,
    navigate,
    pathname,
    search,
    hash,
    state,
    canonicalPath,
    canonicalSearch,
    locationIsCanonical
  ]);
  useEffect(logoutWhenNoShopsAreConfigured, [hasNoAccessibleShops, logout, showNotification, t]);
  useEffect(resetQueriesAfterShopChange, [queryClient, shopId]);

  // Wait until the selected shop is available before rendering the children.
  const contextValue = useMemo(() => {
    if (!shop || !shops) return undefined;
    return { shop, shops, requestShopChange, refetchShops: refetch };
  }, [shop, shops, requestShopChange, refetch]);

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={() => void refetch()}>
            {t("shopContext.retry")}
          </Button>
        }
      >
        {t("global.error.serverUnavailable")}
      </Alert>
    );
  }
  if (isLoading || !contextValue || !locationIsCanonical) return <PageLoading />;
  return <ShopContext.Provider value={contextValue}>{children}</ShopContext.Provider>;

  // Tell OpenFeature which shop is selected, keeping the other context fields unchanged.
  function updateFeatureShop() {
    void setContext(({ shopId: previousShop, ...otherContext }) => {
      if (shopId === undefined) return otherContext;
      return { ...otherContext, shopId };
    });
  }

  function clearFeatureShopOnUnmount() {
    // Clear shopId when this provider unmounts, not when the selected shop changes.
    // Cleanup in updateFeatureShop would clear it before every shop switch.
    return () => {
      void setContext(({ shopId: previousShop, ...otherContext }) => otherContext);
    };
  }

  // Correct the shop ID in the URL if it is missing or the user cannot access that shop.
  function correctShopInUrl() {
    if (shopId === undefined || locationIsCanonical) return;
    // Lazy pages can leave the old location visible while navigation is pending.
    const correction = JSON.stringify([locationKey, canonicalPath, canonicalSearch, hash]);
    if (lastRequestedCorrection.current === correction) return;
    lastRequestedCorrection.current = correction;

    if (
      requestedShopId &&
      !requestedShopIsSelected &&
      lastNotifiedLocationKey.current !== locationKey
    ) {
      lastNotifiedLocationKey.current = locationKey;
      showNotification(t("shopContext.cannotAccess", { shopId: requestedShopId }), "warning");
    }

    void navigate(
      { pathname: canonicalPath, search: canonicalSearch, hash },
      { replace: true, state }
    );
  }

  // Log out if the shop request succeeded but returned no shops.
  function logoutWhenNoShopsAreConfigured() {
    if (!hasNoAccessibleShops) {
      hasHandledEmptyCatalog.current = false;
      return;
    }
    // Do not repeat the toast and logout if React runs this effect again.
    if (hasHandledEmptyCatalog.current) return;
    hasHandledEmptyCatalog.current = true;

    showNotification(t("shopContext.noShops"), "warning");
    logout();
  }

  // Reset queries when switching from one available shop to another.
  function resetQueriesAfterShopChange() {
    const previous = previousShopId.current;
    previousShopId.current = shopId;

    if (previous === undefined || shopId === undefined || previous === shopId) return;
    void queryClient.resetQueries({ stale: false });
  }
};

function resolveAccessibleShop(shops: ShopData[], requestedShopId: string | undefined) {
  const firstAccessibleShop = shops[0];
  if (!requestedShopId) return firstAccessibleShop;

  const requestedShop = shops.find(({ shopId }) => shopId === Number(requestedShopId));
  return requestedShop ?? firstAccessibleShop;
}

function getSearchWithoutShopId(search: string) {
  const params = new URLSearchParams(search);
  params.delete("shop");
  return params.toString();
}
